import Keycloak from 'keycloak-js';
import { runtimeEnv } from '../config/runtimeEnv';
import {
  DEV_LOGIN_ENABLED,
  DEV_LOGIN_PATH,
  devRoleOption,
  devTokenFor,
  getDevRole,
  clearDevRole,
} from './devAuth';
import { consumeForcedExpiry, forcedExpiryError } from './devSession';

/**
 * Both stand-in adapters refresh successfully unless the development switch
 * says otherwise. It is the only way to reach the expired-session path without
 * a realm and a wait — see devSession.ts, which the real adapter never reads.
 */
const stubUpdateToken = (): Promise<boolean> =>
  consumeForcedExpiry() ? Promise.reject(forcedExpiryError()) : Promise.resolve(false);

/**
 * Test-only auth bypass.
 * When the dev server is started with VITE_MOCK_AUTH=true (only done by the
 * Playwright UI tests — never in production builds), `kc` is replaced by a
 * stub that reports an already-authenticated session. This lets the page
 * tests render every route without a Keycloak round-trip; API responses are
 * stubbed by the tests themselves, so no real backend or token is involved.
 */
const MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true';

/** Set by a page test that needs the signed-out screens, before the app loads. */
const MOCK_SIGNED_OUT_KEY = 'mlp.test.signedOut';

const mockSignedOut = (): boolean => {
  try {
    return window.localStorage.getItem(MOCK_SIGNED_OUT_KEY) === 'true';
  } catch {
    return false;
  }
};

const createMockKeycloak = (): Keycloak => {
  const mock = {
    get authenticated() {
      return !mockSignedOut();
    },
    token: 'mock-token',
    tokenParsed: {
      sub: 'mock-user',
      preferred_username: 'testuser',
      given_name: 'Test',
      family_name: 'User',
      email: 'test@example.com',
    },
    init: () => Promise.resolve(true),
    login: () => Promise.resolve(),
    register: () => Promise.resolve(),
    logout: () => Promise.resolve(),
    updateToken: stubUpdateToken,
  };
  return mock as unknown as Keycloak;
};

/**
 * Local development login bypass.
 * When VITE_DEV_LOGIN=true (only in .env.example), Keycloak is replaced by a stub
 * driven entirely by the role stored in localStorage (see devAuth.ts). No role
 * selected → reports unauthenticated, so the sign-in card is shown, and its
 * buttons open the DevLoginScreen where Keycloak's page would be. A role
 * selected → reports authenticated and emits a `devbypass.<role>` bearer token
 * that the API accepts (only while DEV_AUTH_BYPASS=true). Real users' JWT flow
 * is untouched in every other build.
 */
const createDevKeycloak = (): Keycloak => {
  const stub = {
    get authenticated() {
      return getDevRole() !== null;
    },
    get token() {
      const role = getDevRole();
      return role ? devTokenFor(role) : undefined;
    },
    get tokenParsed() {
      const role = getDevRole();
      if (!role) return undefined;
      const opt = devRoleOption(role);
      return {
        sub: `dev-${role}`,
        preferred_username: `dev-${role}`,
        given_name: opt.firstName,
        family_name: opt.lastName,
        email: `dev-${role}@localhost.dev`,
      };
    },
    init: () => Promise.resolve(getDevRole() !== null),
    login: () => {
      window.location.assign(DEV_LOGIN_PATH);
      return Promise.resolve();
    },
    register: () => {
      window.location.assign(DEV_LOGIN_PATH);
      return Promise.resolve();
    },
    logout: () => {
      clearDevRole();
      window.location.assign(window.location.origin);
      return Promise.resolve();
    },
    updateToken: stubUpdateToken,
  };
  return stub as unknown as Keycloak;
};

/**
 * The real client. Its three addresses come from the runtime configuration, not
 * from the bundle: staging and production run the same image and must reach
 * different realms. A build-time constant here would send every deployment to
 * whichever realm was compiled in — see config/runtimeEnv.ts.
 */
const NO_REALM =
  'No Keycloak realm configured. A deployed build reads this from /config.js; ' +
  'locally, set VITE_KEYCLOAK_URL, VITE_KEYCLOAK_REALM and ' +
  'VITE_KEYCLOAK_CLIENT_ID to the realm you mean, or set VITE_DEV_LOGIN=true ' +
  'to use the persona picker instead. There is no default on purpose — see ' +
  'docs/decisions/0004-runtime-config-for-the-spa.md.';

/**
 * Stands in for the adapter when no realm was named, and throws on the calls
 * that would reach one. It throws on use rather than on construction because
 * `kc` is built at module load: throwing there takes down anything that merely
 * imports this file, including tests that never sign in.
 */
const createUnconfiguredKeycloak = (): Keycloak => {
  const refuse = (): never => {
    throw new Error(NO_REALM);
  };
  return {
    authenticated: false,
    token: undefined,
    tokenParsed: undefined,
    init: refuse,
    login: refuse,
    register: refuse,
    logout: refuse,
    updateToken: refuse,
  } as unknown as Keycloak;
};

const createRealKeycloak = (): Keycloak => {
  // A development build has no ConfigMap, so runtimeEnv leaves these empty
  // unless someone named a realm. Refuse rather than sign in somewhere
  // unintended: with the bypass off, the next click opens a real login.
  if (
    !runtimeEnv.keycloakUrl ||
    !runtimeEnv.keycloakRealm ||
    !runtimeEnv.keycloakClientId
  ) {
    return createUnconfiguredKeycloak();
  }
  return new Keycloak({
    url: runtimeEnv.keycloakUrl,
    realm: runtimeEnv.keycloakRealm,
    clientId: runtimeEnv.keycloakClientId,
  });
};

export const kc = DEV_LOGIN_ENABLED
  ? createDevKeycloak()
  : MOCK_AUTH
    ? createMockKeycloak()
    : createRealKeycloak();

export default kc;
